/* eslint-disable @typescript-eslint/brace-style */
import { useNavigate } from 'react-router-dom';
import { UserProfile } from 'api/account/models/UserProfile';
import { Paged } from 'api/common/types/Page';
import RolesService from 'api/roles/RolesService';
import { RoleDto } from 'api/roles/models/RoleDto';
import { RoleSearchCriteria } from 'api/roles/models/RoleSearchCriteria';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import Logger from 'common/services/Logger';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import styles from './RolesScreen.module.scss';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import toast from 'react-hot-toast';
import { Button, Container, Dropdown, Stack, Form, Card } from 'react-bootstrap';
import Loading from 'common/services/Loading';
import UsersService from 'api/users/UsersService';

type Props = {
};

const RolesScreen: React.FC<Props> = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [criteria, setCriteria] = useState<RoleSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderBy: 'asc',
        orderColumn: 'r.name'
    });
    const [rolesPage, setRolesPage] = useState<Paged<RoleDto>>();
    const roleProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasRolesWritePolicy = UsersService.hasPolicies(roleProfile?.policies || [], ['ROLES_WRITE']);
    const [filters, setFilters] = useState({
        allIn: ''
    });
    const getData = async () => {
        try {
            Loading.show();
            const page = await RolesService.getList(criteria);
            setRolesPage(page);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get roles list', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    }

    const renderTableActionCell = (row: RoleDto) => {
        return <Dropdown>
            <Dropdown.Toggle id="dropdown-basic-button" size="sm" variant="outline-secondary">...</Dropdown.Toggle>
            <Dropdown.Menu popperConfig={{ strategy: 'fixed' }} renderOnMount>
                <Dropdown.Item onClick={() => navigate(`/security/roles/details/${row.id}`)}>{t('common.details')}</Dropdown.Item>
                {hasRolesWritePolicy && !row.readOnly && <>
                    <Dropdown.Item onClick={() => navigate(`/security/roles/edit/${row.id}`)}>{t('common.edit')}</Dropdown.Item>
                    <Dropdown.Item onClick={() => showRemoveItemDialog(row)}>{t('common.delete')}</Dropdown.Item>
                </>
                }
            </Dropdown.Menu>
        </Dropdown>
    };

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    }

    const tableColumns: ListingTableColumn<RoleDto>[] = [
        {
            field: 'name',
            name: t('security.roles.list.name') ?? '',
            onSearch: onTableFilter,
            searchField: 'r.name'
        },
        {
            name: '',
            width: '60px',
            cellAlignment: 'right',
            preventClick: true,
            renderCell: renderTableActionCell,
            cellStyle: { overflow: 'unset' }
        },
    ];

    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<RoleDto | null>(null);

    const showRemoveItemDialog = async (item: RoleDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onRowClick = (event: any, id?: string) => {
        if (id) {
            navigate(`/security/roles/details/${id}`);
        }
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            toast.error(t('messages.record_delete_error'));
            return;
        }
        try {
            await RolesService.remove(itemToRemove);
            onCancelRemove();
            toast.success(t('messages.record_delete_success'));
            setCriteria({ ...criteria, page: 1 })
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete roles', error);
            toast.error(t('messages.record_delete_error'));
        }
    };

    useEffect(() => {
        getData();
    }, [criteria]);

    function handleChange(e: any) {
        const key = e.target.name;
        const value = e.target.value;
        setFilters({ ...filters, [key]: value });
    }
    function clearFilters() {
        setFilters({ allIn: '' });
        setCriteria({ ...criteria, page: 1, allIn: '' });
    }
    const onSearch = () => {
        setCriteria({ ...criteria, allIn: filters.allIn });
    }

    return (
        <ScreenTitle title={t('security.roles.title')}>
            <Container fluid>
                <Card className={styles.filters}>
                    <Card.Body>
                        <Stack direction="horizontal" gap={3} >
                            <Form.Control type="text" value={filters.allIn} className="me-auto" name="allIn" onChange={handleChange} onKeyDown={(e: any) => { if (e.key === 'Enter') onSearch(); }} placeholder={t('common.write_here')} />
                            <Button variant="secondary" onClick={onSearch}>{t('common.search')}</Button>
                            <div className="vr" />
                            <Button variant="outline-danger" onClick={clearFilters}>{t('common.clear')}</Button>
                            <Button variant="primary" onClick={() => navigate('/security/roles/new/')}>{t('common.new')}</Button>
                        </Stack>
                    </Card.Body>
                </Card>
            </Container>
            <Container fluid>
                <ListingTable
                    columns={tableColumns}
                    rows={rolesPage?.items || []}
                    onRowClick={(row, _, event) => onRowClick(event, row.id)}
                    allowHover={true}
                    initialSearch={{ colField: 'u.real_name', isOrderAsc: true }}
                />
                <PaginationWithInfo
                    itemName={t('security.roles.title')}
                    currentPage={rolesPage?.currentPage ?? 1}
                    pageItems={rolesPage?.items.length || 0}
                    totalItems={rolesPage?.totalItems || 0}
                    onChange={page => setCriteria({ ...criteria, page: page })}
                />
            </Container>
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('messages.remove_record_with_ident', { name: itemToRemove?.name ?? '' })} />
        </ScreenTitle>
    );
};

export default RolesScreen;
